import type { MFAAction } from '@dynamic-labs/sdk-api-core';

import { getDefaultClient } from '../../../client/defaultClient';
import { getMfaMethods } from '../getMfaMethods';

type IsMfaRequiredForActionParams = {
  mfaAction: MFAAction;
};

/**
 * Checks if MFA is required for a specific action.
 *
 * This function determines whether multi-factor authentication is required
 * for the specified action based on project settings and user configuration.
 *
 * @param params.mfaAction - The action to check MFA requirements for.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to true if MFA is required for the action, false otherwise.
 */
export const isMfaRequiredForAction = async (
  { mfaAction }: IsMfaRequiredForActionParams,
  client = getDefaultClient()
) => {
  const projectSettings = client.projectSettings;

  const isRequiredForAction = projectSettings?.security?.mfa?.actions?.some(
    (action) => action.action === mfaAction && action.required
  );

  // if mfa is not required for the action, return false
  // no matter if general mfa is set to required or not
  if (!isRequiredForAction) {
    return false;
  }

  const isMfaMethodRequired = projectSettings?.security?.mfa?.required;

  // if mfa is required for the action and also set as required in general, return true
  if (isMfaMethodRequired) {
    return true;
  }

  const mfaMethods = await getMfaMethods(client);
  // if general mfa is optional, but user has registered methods,
  // then it should be required for the action
  return mfaMethods.userHasVerifiedMfaMethods;
};
